import React, { useState, useEffect } from "react"
import { useTranslation } from 'react-i18next'
import styles from "./ProjectProgressBar.module.css"
import InstantFeedback from './components/InstantFeedback'
import { 
  FiCircle, 
  FiCheckCircle, 
  FiArrowRight, 
  FiCode, 
  FiFileText,
  FiSettings,
  FiLayers,
  FiServer,
  FiTarget
} from 'react-icons/fi'

const ProjectProgressBar = ({
  currentState,
  shouldFlash,
  handleBackToPreviousPhase,
  flowName,
  resetHistory,
  feedbackDelta,
  onFeedbackComplete,
}) => {
  const { t } = useTranslation();
  // 使用sessionStorage保存状态历史，但只在当前状态非空时才恢复历史
  const [stateHistory, setStateHistory] = useState(() => {
    try {
      // 只有当currentState非空时才尝试从sessionStorage恢复历史
      if (currentState) {
        const savedHistory = sessionStorage.getItem('projectStateHistory');
        const parsedHistory = savedHistory ? JSON.parse(savedHistory) : [];
        
        // 验证恢复的历史是否包含当前状态，如果不包含则清空历史
        if (parsedHistory.length > 0 && !parsedHistory.includes(currentState)) {
          console.log('当前状态与历史不匹配，清空历史');
          sessionStorage.removeItem('projectStateHistory');
          return [currentState]; // 只保留当前状态
        }
        return parsedHistory;
      }
      return []; // 如果当前状态为空，不恢复历史
    } catch (error) {
      console.error('读取状态历史出错:', error);
      return currentState ? [currentState] : [];
    }
  });
  
  const [isFlashing, setIsFlashing] = useState(false);
  const [currentFlow, setCurrentFlow] = useState(flowName);
  const scrollContainerRef = React.useRef(null); // 引用滚动容器

  // 监听 stateHistory 变化，自动滚动到最右侧
  useEffect(() => {
    if (scrollContainerRef.current) {
      // 使用平滑滚动效果
      scrollContainerRef.current.scrollTo({
        left: scrollContainerRef.current.scrollWidth,
        behavior: 'smooth'
      });
    }
  }, [stateHistory]);

  // 监听resetHistory和flowName变化，强制重置历史
  useEffect(() => {
    if (resetHistory || (flowName && flowName !== currentFlow)) {
      console.log('流程类型变化或强制重置，清空历史', {flowName, currentFlow, resetHistory});
      
      // 更新当前流程类型
      setCurrentFlow(flowName);
      
      // 当重置标志为true时，完全清空历史，不保留任何状态
      if (resetHistory) {
        setStateHistory([]);
        try {
          sessionStorage.removeItem('projectStateHistory');
        } catch (error) {
          console.error('清除状态历史出错:', error);
        }
      } 
      // 流程变化但非强制重置时，可以保留当前状态
      else if (flowName && flowName !== currentFlow) {
        // 清空历史，只保留当前状态(如果有)
        if (currentState) {
          setStateHistory([currentState]);
          try {
            sessionStorage.setItem('projectStateHistory', JSON.stringify([currentState]));
          } catch (error) {
            console.error('保存重置后的状态历史出错:', error);
          }
        } else {
          setStateHistory([]);
          try {
            sessionStorage.removeItem('projectStateHistory');
          } catch (error) {
            console.error('清除状态历史出错:', error);
          }
        }
      }
    }
  }, [resetHistory, flowName, currentState, currentFlow]);

  // 保存状态历史到sessionStorage
  useEffect(() => {
    if (stateHistory.length > 0) {
      try {
        sessionStorage.setItem('projectStateHistory', JSON.stringify(stateHistory));
      } catch (error) {
        console.error('保存状态历史出错:', error);
      }
    }
  }, [stateHistory]);

  // 更新历史状态的逻辑
  useEffect(() => {
    if (currentState) {
      console.log('📊 ProjectProgressBar 接收到新状态:', currentState);
      console.log('📊 当前历史:', stateHistory);
      
      const currentIndex = stateHistory.indexOf(currentState);

      if (currentIndex === -1) {
        // 如果当前状态不存在于历史记录中，直接添加到历史记录末尾
        console.log('📊 添加新状态到历史记录:', currentState);
        setStateHistory((prev) => [...prev, currentState]);
      } else if (currentIndex < stateHistory.length - 1) {
        // 如果回档了（即 currentState 出现在历史记录中，但不是最后一个），则移除所有比当前状态更新的状态
        console.log('📊 回退到历史状态:', currentState);
        setStateHistory((prev) => prev.slice(0, currentIndex + 1));
      } else {
        console.log('📊 状态已存在且是最新的:', currentState);
      }
    } else {
      console.log('📊 ProjectProgressBar 当前状态为空');
    }
  }, [currentState, stateHistory]);

  // 监听 shouldFlash 的变化来触发闪烁
  useEffect(() => {
    if (shouldFlash) {
      setIsFlashing(true)
      setTimeout(() => {
        setIsFlashing(false)
      }, 2000) // 缩短闪烁时间
    }
  }, [shouldFlash])
  
  const backToPreviousPhase = (state) => {
    if (state !== currentState) {
      handleBackToPreviousPhase(state)
    }
  }

  // 阶段显示名 i18n 映射（内部值 -> 翻译键）
  const PHASE_DISPLAY_KEYS = { '新需求': 'phase_new_requirement' };
  const getPhaseDisplayName = (state) => PHASE_DISPLAY_KEYS[state] ? t(PHASE_DISPLAY_KEYS[state]) : state;

  // 阶段关键字匹配（中英文，不区分大小写）
  const matchRequirement = (st) => { const l = (st||'').toLowerCase(); return st.includes('需求') || l.includes('requirement') || l.includes('creative'); };
  const matchPlanning = (st) => { const l = (st||'').toLowerCase(); return st.includes('规划') || st.includes('计划') || l.includes('planning') || l.includes('strategy'); };
  const matchDesign = (st) => { const l = (st||'').toLowerCase(); return st.includes('设计') || l.includes('design'); };
  const matchCoding = (st) => { const l = (st||'').toLowerCase(); return st.includes('编码') || st.includes('开发') || l.includes('coding') || l.includes('development') || l.includes('implementation'); };
  const matchTesting = (st) => { const l = (st||'').toLowerCase(); return st.includes('测试') || l.includes('testing') || l.includes('evaluation') || l.includes('review'); };
  const matchDeploy = (st) => { const l = (st||'').toLowerCase(); return st.includes('部署') || st.includes('发布') || l.includes('deploy') || l.includes('release') || l.includes('operation'); };

  // 获取阶段对应的CSS类名
  const getStageClassName = (state) => {
    if (!state) return '';
    
    if (matchRequirement(state)) return styles.requirementStage;
    if (matchPlanning(state)) return styles.planningStage;
    if (matchDesign(state)) return styles.designStage;
    if (matchCoding(state)) return styles.codingStage;
    if (matchTesting(state)) return styles.testingStage;
    if (matchDeploy(state)) return styles.deployStage;
    
    return '';
  };

  // 获取状态对应的图标
  const getStateIcon = (state, isActive) => {
    if (!state) return <FiCircle className={styles.stateIcon} />;

    // 根据状态返回相应图标（中英文关键字匹配）
    const icon = (IconComponent) => <IconComponent className={styles.stateIcon} />;
    if (matchRequirement(state)) return icon(FiFileText);
    if (matchPlanning(state)) return icon(FiTarget);
    if (matchDesign(state)) return icon(FiLayers);
    if (matchCoding(state)) return icon(FiCode);
    if (matchTesting(state)) return icon(FiSettings);
    if (matchDeploy(state)) return icon(FiServer);
    return isActive ? icon(FiCheckCircle) : icon(FiCircle);
  }

  return (
    <div className={styles.progressWrapper}>
      <div className={styles.progressContainer} ref={scrollContainerRef}>
        {stateHistory.map((state, index) => (
          <React.Fragment key={index}>
            {index > 0 && <FiArrowRight className={styles.arrow} />}
            <div
              className={`${styles.stateItem} ${
                state === currentState ? styles.active : ""
              } ${state === currentState && isFlashing ? styles.flashing : ""} ${getStageClassName(state)}`}
              onDoubleClick={() => backToPreviousPhase(state)}
              title={t('progress_bar_rollback_title', { state: getPhaseDisplayName(state) })}
            >
              {getStateIcon(state, state === currentState)}
              <span>{getPhaseDisplayName(state)}</span>
            </div>
          </React.Fragment>
        ))}
      </div>
      
      {/* 即时反馈区域 - 移到进度条外部右侧 */}
      <div className={styles.instantFeedbackArea}>
        <InstantFeedback
          delta={feedbackDelta}
          onAnimationComplete={onFeedbackComplete}
        />
      </div>
    </div>
  )
}

export default ProjectProgressBar
